import { useDisclosure } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTableActions from '../../../components/Monetize/DataTable/MDataTableActions';
import { getAccountPaymentMethodCreate } from '../../../constants/routes';
import useCurrentPaymentGateways from '../../../hooks/useCurrentPaymentGateways';
import { useFlags } from '../../../services/launchDarkly';
import { GatewayAccountLinkModal } from './GatewayAccountLinkModal';
import { GatewayAccountUnlinkModal } from './GatewayAccountUnlinkModal';

interface PaymentMethodListActionsProps {
  accountId: string;
  gatewayCustomerId?: string;
}
export const PaymentMethodListActions: React.FC<
  PaymentMethodListActionsProps
> = ({ accountId, gatewayCustomerId }) => {
  const {
    open: isGatewayAccountUnlinkModalOpen,
    onOpen: onGatewayAccountUnlinkModalOpen,
    onClose: onGatewayAccountUnlinkModalClose,
  } = useDisclosure();

  const {
    open: isGatewayAccountLinkModalOpen,
    onOpen: onGatewayAccountLinkModalOpen,
    onClose: onGatewayAccountLinkModalClose,
  } = useDisclosure();

  const navigate = useNavigate();
  const { enableGatewayAccountUnlink, enableGatewayAccountLink } = useFlags();

  const { currentPaymentGateway } = useCurrentPaymentGateways({
    meta: { showErrorToast: true },
    enabled: true,
  });

  // Memoize the navigation action
  const handleNewPaymentMethod = useMemo(
    () => () => navigate(getAccountPaymentMethodCreate(accountId)),
    [navigate, accountId],
  );

  // Memoize the actions array
  const actions = useMemo(() => {
    const hasPaymentGateway = Boolean(currentPaymentGateway?.id);
    const hasGatewayCustomer = Boolean(gatewayCustomerId);
    const canUnlinkGateway =
      enableGatewayAccountUnlink && hasPaymentGateway && hasGatewayCustomer;
    const canLinkGateway =
      enableGatewayAccountLink && hasPaymentGateway && !hasGatewayCustomer;

    return [
      {
        enabled: true,
        title: 'New Payment Method',
        action: handleNewPaymentMethod,
      },
      {
        enabled: canUnlinkGateway,
        title: 'Unlink Payment Gateway Account',
        action: onGatewayAccountUnlinkModalOpen,
      },
      {
        enabled: canLinkGateway,
        title: 'Link Payment Gateway Account',
        action: onGatewayAccountLinkModalOpen,
      },
    ];
  }, [
    currentPaymentGateway?.id,
    gatewayCustomerId,
    enableGatewayAccountUnlink,
    enableGatewayAccountLink,
    handleNewPaymentMethod,
    onGatewayAccountUnlinkModalOpen,
    onGatewayAccountLinkModalOpen,
  ]);

  return (
    <>
      {currentPaymentGateway?.id &&
        gatewayCustomerId &&
        isGatewayAccountUnlinkModalOpen && (
          <GatewayAccountUnlinkModal
            onClose={onGatewayAccountUnlinkModalClose}
            accountId={accountId}
            paymentGatewayId={currentPaymentGateway?.id}
            gatewayCustomerId={gatewayCustomerId}
          />
        )}

      {currentPaymentGateway?.id &&
        !gatewayCustomerId &&
        isGatewayAccountLinkModalOpen && (
          <GatewayAccountLinkModal
            onClose={onGatewayAccountLinkModalClose}
            accountId={accountId}
            paymentGatewayId={currentPaymentGateway?.id}
          />
        )}

      <DataTableActions actions={actions} />
    </>
  );
};
